<template>
  <div class="c-integration-settings-partner-settings">
    <template v-if="hasSegmentGrouping">
      <IntegrationSettingsPopulationSegment
        :populations="populationsWithSegments"
        :checked-population-ids="checkedNumericIds"
        @population-checked="handlePopulationChecked"
        @populations-batch-checked="handleBatchChecked"
        :is-disabled="isDisabled"
      />
    </template>
    <template v-else>
      <BittsCheckbox
        v-for="population in populations"
        :checked="checkedKeys.includes(population.id)"
        :disabled="isDisabled"
        :key="population.id"
        :label="population.name"
        @input="(isChecked: boolean) => boxChecked(population.id, isChecked)"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsCheckbox } from '@crossbeam/bitts';

import { computed } from 'vue';

import IntegrationSettingsPopulationSegment from '@/components/integrations/IntegrationSettingsPopulationSegment.vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { ALL_STANDARD_POPULATIONS } from '@/constants/standard_populations';
import { useFeatureFlagStore } from '@/stores';
import { MinimalIntegrationProfile } from '@/types/integration_profiles';
import type { Population } from '@/types/populations';

const props = withDefaults(
  defineProps<{
    integrationProfileSettings?: MinimalIntegrationProfile | null;
    isDisabled?: boolean;
    settingType: string;
  }>(),
  { integrationProfileSettings: undefined, isDisabled: false },
);

const emit =
  defineEmits<
    (e: 'update-settings', payload: MinimalIntegrationProfile) => void
  >();

const featureFlagStore = useFeatureFlagStore();
const hasSegmentGrouping = featureFlagStore.hasFeatureFlag(
  FEATURE_FLAG.TEMP_UPDATED_DATA_PUSH_POP_TYPES,
);

const checkedKeys = computed<string[]>(() => {
  return (
    props.integrationProfileSettings?.standard_population_settings
      .filter((pop) => pop.is_included)
      .map((pop) => pop.standard_type) ?? []
  );
});

const populations = [
  ...ALL_STANDARD_POPULATIONS.map((standardPop) => ({
    id: standardPop.standard_type,
    name: standardPop.name,
  })),
  {
    id: 'custom',
    name: 'Custom Populations',
  },
];

// Create a hash of string ID to numeric ID for the tree component
// Use negative IDs to avoid collision with real population IDs from the database
const stringToNumericIdMap = computed(() => {
  const map = new Map<string, number>();
  ALL_STANDARD_POPULATIONS.forEach((pop, index) => {
    map.set(pop.standard_type, -(index + 1));
  });
  map.set('custom', -(ALL_STANDARD_POPULATIONS.length + 1));
  return map;
});

const numericToStringIdMap = computed(() => {
  const map = new Map<number, string>();
  ALL_STANDARD_POPULATIONS.forEach((pop, index) => {
    map.set(-(index + 1), pop.standard_type);
  });
  map.set(-(ALL_STANDARD_POPULATIONS.length + 1), 'custom');
  return map;
});

// Convert populations to Population-like objects with numeric IDs
// When feature flag is on, we only show the segment labels (including "Other")
// "Custom Populations" is not shown separately - the "Other" segment represents it
const populationsWithSegments = computed<Population[]>(() => {
  return ALL_STANDARD_POPULATIONS.map((standardPop, index) => ({
    id: -(index + 1),
    name: standardPop.name,
    segment: standardPop.standard_type,
    standard_type: standardPop.standard_type,
  })) as Population[];
});

const checkedNumericIds = computed(() => {
  return checkedKeys.value
    .map((key) => stringToNumericIdMap.value.get(key))
    .filter((id): id is number => id !== undefined);
});

const updatePopulationSettings = (id: string, isChecked: boolean) => {
  return {
    population_settings:
      props.integrationProfileSettings?.population_settings ?? [],
    standard_population_settings: [
      ...(props.integrationProfileSettings?.standard_population_settings.map(
        (pop) => ({
          ...pop,
          is_included:
            pop.standard_type === id
              ? isChecked
              : checkedKeys.value.includes(pop.standard_type),
          config_type: props.settingType,
        }),
      ) ?? []),
    ],
  };
};

const boxChecked = (id: string, isChecked: boolean) => {
  const payload = updatePopulationSettings(id, isChecked);
  emit('update-settings', payload);
};

function handlePopulationChecked(numericId: number, isChecked: boolean) {
  const stringId = numericToStringIdMap.value.get(numericId);
  if (stringId) {
    boxChecked(stringId, isChecked);
  }
}

function handleBatchChecked(numericIds: number[], isChecked: boolean) {
  const stringIds = numericIds
    .map((id) => numericToStringIdMap.value.get(id))
    .filter((id): id is string => id !== undefined);

  const currentChecked = new Set(checkedKeys.value);

  if (isChecked) {
    stringIds.forEach((id) => currentChecked.add(id));
  } else {
    stringIds.forEach((id) => currentChecked.delete(id));
  }

  const payload = {
    population_settings:
      props.integrationProfileSettings?.population_settings ?? [],
    standard_population_settings: [
      ...(props.integrationProfileSettings?.standard_population_settings.map(
        (pop) => ({
          ...pop,
          is_included: currentChecked.has(pop.standard_type),
          config_type: props.settingType,
        }),
      ) ?? []),
    ],
  };

  emit('update-settings', payload);
}
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.c-integration-settings-partner-settings {
  @apply flex flex-col gap-4 mt-8;
}
</style>
